import axios from "axios";
import { z } from "zod";
import { API_BASE_URL, AUTH_REFRESH_PATH } from "./config";
import { setAccessToken } from "./auth-seam";
import { parseWith } from "../../parse";

// Refresh returns the full profile too; we only need the new access token here. A later
// phase consumes the profile fields (capabilities / available_surfaces) from this response.
const refreshResponseSchema = z.object({
  access: z.string(),
  access_expires_in: z.number(),
});

// SINGLE-FLIGHT: the one shared in-flight refresh promise. Concurrent 401s all await this
// same promise instead of firing parallel refreshes — the backend rotates the refresh token
// and treats a second concurrent use as replay, which revokes the entire session family.
let inFlight: Promise<string> | null = null;

/**
 * Refresh the access token, serialized. Concurrent callers share ONE network round-trip;
 * the in-flight promise is cleared once it settles so the next 401 can refresh again.
 * Resolves with the new access token; rejects (propagating the axios error) on failure.
 */
export function refreshAccessToken(): Promise<string> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function performRefresh(): Promise<string> {
  // Bare axios (no interceptors) so the refresh call can never itself trigger the
  // 401→refresh loop. No request body — the browser attaches the httpOnly dz_refresh cookie
  // automatically because the path is under /api/v1/auth/ and credentials are included.
  const response = await axios.post<unknown>(
    `${API_BASE_URL}${AUTH_REFRESH_PATH}`,
    null,
    {
      withCredentials: true,
    }
  );
  const { access } = parseWith(
    refreshResponseSchema,
    response.data,
    "POST /auth/token/refresh/"
  );
  setAccessToken(access);
  return access;
}
